import React, { useEffect, useRef, useState } from 'react';
import DocumentTitle from 'react-document-title';
import { useModal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Banner,
  SingleSelect,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Box,
  Button,
  NumericInput,
  Paper,
  Stack,
  SubmitButton,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AlertInfoIcon,
  MoneyIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import RentForecastModal from './RentForecastModal';
import useIntl from '../../hooks/useIntl';
import TableLayout from '../../components/TableLayout';
import RentForecastHeader from './RentForecastHeader';
import RentForecastTable from './RentForecastTable';
import RDBudgetModal from './RDBudgetModal';
import messages from './messages';
import { useManageRentForecast } from './hooks';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { AppContext } from '../App/context';
import UtilityAllowanceTableModal from './UtilityAllowanceTable/modal';
import ProposedChangesModal from './ProposedChangesModal';
import isEqual from 'lodash.isequal';
import BudgetActionsDropdown from './BudgetActionsDropdown';
import ResidentLetterModal from './ResidentLetterModal';
import AuthorizeSignatureModal from './AuthorizeSignatureModal';
import isUndefined from 'lodash/isUndefined';
import pick from 'lodash/pick';
import { FormattedMessage } from 'react-intl';
import ApproveBudgetModal from './ApproveBudgetModal';
import BuildBudget from './BuildBudget';
import ReportHistoryModal from './ReportHistoryModal';
import TransmissionCard from './TransmissionCard';
import { DeleteForecastButton } from 'containers/RentForecast/DeleteForecast';
import { getForecastName } from './utils';

const Percent = () => <Box sx={{ paddingLeft: 1 }}>%</Box>;

const Card = (props) => {
  const {
    title,
    actualValue,
    proposedValue: initialProposedValue,
    valueKey,
    adornment,
    tooltip,
    sx,
    updateRentForecast,
    currentRentForecastId,
    subtitle,
    onChangeApplied,
    disabled,
  } = props;
  const [editState, setEditState] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const intl = useIntl();
  const normalize = (v) => (v === null || v === undefined ? '' : v);
  const [proposedValue, setProposedValue] = useState(
    normalize(initialProposedValue),
  );
  const [valueChanged, setValueChanged] = useState(false);
  useEffect(() => {
    setProposedValue(normalize(initialProposedValue));
  }, [initialProposedValue, currentRentForecastId]);

  const handleProposedValueChange = (e) => {
    const newValue =
      e.target.value === ''
        ? null
        : Number(parseFloat(e.target.value).toFixed(2));
    setValueChanged(true);
    setProposedValue(newValue);
  };

  const handleUpdateProposedValue = () => {
    if (!valueChanged) return;
    setSubmitting(true);
    updateRentForecast(
      {
        id: currentRentForecastId,
        [valueKey]: proposedValue,
      },
      {
        onSuccess: () => {
          setEditState(false);

          if (onChangeApplied) {
            onChangeApplied(proposedValue);
          }
        },
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
    setValueChanged(false);
  };

  const handleCancel = () => {
    setValueChanged(false);
    setEditState(false);
    setProposedValue(normalize(initialProposedValue));
  };

  return (
    <Paper
      sx={{
        ...sx,
        padding: 3,
        width: 300,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex' }}>
          <Typography variant="labelLarge" sx={{ marginRight: 0.5 }}>
            <FormattedMessage {...title} />
          </Typography>
          <Tooltip title={intl.formatMessage(tooltip)} placement="bottom" arrow>
            <AlertInfoIcon
              sx={{
                color: 'text.secondary',
              }}
            />
          </Tooltip>
        </Box>
        {subtitle && (
          <Typography variant="labelLarge">
            <FormattedMessage {...subtitle} />
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box
          sx={{
            display: isUndefined(actualValue) ? 'none' : 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="labelMedium" gutterBottom>
            {intl.formatMessage(messages.actual)}
          </Typography>
          <Typography>{actualValue}</Typography>
        </Box>
        <Box sx={{ width: 148 }}>
          <Typography variant="labelMedium" gutterBottom>
            {intl.formatMessage(messages.proposed)}
          </Typography>
          <NumericInput
            value={proposedValue}
            onChange={handleProposedValueChange}
            placeholder=""
            startAdornment={adornment}
            step="0.01"
            gutterBottom={false}
            HelperTextProps={{
              sx: { display: 'none' },
            }}
            readOnly={!editState}
            disabled={submitting}
          />
        </Box>
      </Box>
      <Box display="flex" flexDirection="row-reverse">
        {editState ? (
          <>
            <SubmitButton
              onClick={handleUpdateProposedValue}
              isSubmitting={submitting}
              variant="text"
            >
              <FormattedMessage {...messages.cardSaveLabel} />
            </SubmitButton>
            <Button
              onClick={handleCancel}
              disabled={submitting}
              sx={{ margin: '0 8px' }}
              variant="text"
            >
              <FormattedMessage {...messages.cardCancelLabel} />
            </Button>
          </>
        ) : (
          <Button
            onClick={() => setEditState(true)}
            variant="text"
            disabled={disabled}
          >
            <FormattedMessage {...messages.cardEditLabel} />
          </Button>
        )}
      </Box>
    </Paper>
  );
};

type Props = {
  history: Object,
};

const RentForecast = ({ history }: Props) => {
  const {
    open: rentForecastOpen,
    openModal: openRentForecastModal,
    closeModal: closeRentForecastModal,
  } = useModal();
  const {
    open: rdBudgetOpen,
    openModal: openRDBudgetModal,
    closeModal: closeRDBudgetModal,
  } = useModal();

  const {
    open: intacctOpen,
    openModal: openIntacctModal,
    closeModal: closeIntacctModal,
  } = useModal();
  const {
    open: proposedChangesOpen,
    openModal: openProposedChangesModal,
    closeModal: closeProposedChangesModal,
  } = useModal();
  const {
    open: residentLetterOpen,
    openModal: openResidentLetterModal,
    closeModal: closeResidentLetterModal,
  } = useModal();
  const {
    open: authorizeSignatureOpen,
    openModal: openAuthorizeSignatureModal,
    closeModal: closeAuthorizeSignatureModal,
  } = useModal();
  const {
    open: approveBudgetOpen,
    openModal: openApproveBudgetModal,
    closeModal: closeApproveBudgetModal,
  } = useModal();
  const {
    open: buildBudgetOpen,
    openModal: openBuildBudgetModal,
    closeModal: closeBuildBudgetModal,
  } = useModal();
  const {
    open: reportHistoryOpen,
    openModal: openReportHistoryModal,
    closeModal: closeReportHistoryModal,
  } = useModal();

  const intl = useIntl();
  const { rdBudgets = false, forecastRents = false } = useFlags();
  const ldClient = useLDClient();
  useEffect(() => {
    if (!ldClient || !ldClient.initialized) return;

    if (!forecastRents) {
      history.push('/404');
    }
  }, [ldClient, forecastRents]);
  const appContext = React.useContext(AppContext);
  const isRD = React.useMemo(
    () =>
      appContext.selectedProperty.pap.find(
        (fpap) => fpap?.masterAffordableProgram?.name === 'RD',
      ),
    [appContext.selectedProperty],
  );

  const manageRentForecastData = useManageRentForecast({ isRD });

  const {
    allColumnsHidden,
    columnOptions,
    count,
    createRentForecast,
    currentRentForecast,
    dateState,
    filteredHeaders,
    filterState,
    filterTypeState,
    footerRow,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    handleSettingsClose,
    headers,
    isLoading,
    onCSVButtonClick,
    order,
    orderBy,
    rentForecasts,
    resetProposedState,
    rows,
    searchState,
    selectedColumns,
    selectedProposedChangeType,
    setCurrentRentForecast,
    setSelectedProposedChangeType,
    tableName,
    totalCount,
    unitInfo,
    updateRentForecast,
    submitDirectRentUpdate,
    isQueryLoading,
    isUpdatingDirect,
    setProposedState,
    reloadRentForecastList,
    loadingFloorPlans,
    setCurrentRentForecastId,
    hasEditPermission,
    canEdit,
  } = manageRentForecastData;

  const prevPayloadRef = useRef([]);
  const skipNextSync = useRef(false);
  const prevTypeRef = useRef(selectedProposedChangeType);
  const prevForecastSync = useRef(currentRentForecast?.id);

  const forecastsWithApprovalNumbers = React.useMemo(() => {
    if (!rentForecasts) {
      return {};
    }

    return rentForecasts.rentForecasts.reduce((acc, forecast) => {
      if (forecast.approvedFrom) {
        acc[forecast.approvedFrom] = acc.hasOwnProperty(forecast.approvedFrom);
      }

      return acc;
    }, {});
  }, [rentForecasts?.rentForecasts.length]);

  const hasApprovals = React.useMemo(
    () =>
      rentForecasts?.rentForecasts.some(
        (rf) => rf.approvedFrom === currentRentForecast?.id,
      ) ?? false,
    [currentRentForecast, rentForecasts?.rentForecasts.length],
  );

  useEffect(() => {
    if (!currentRentForecast?.id) return;

    prevPayloadRef.current = [];
    prevForecastSync.current = currentRentForecast.id;
    skipNextSync.current = true;
    resetProposedState({});
  }, [currentRentForecast?.id, resetProposedState]);

  useEffect(() => {
    setRentSpreadValue(currentRentForecast?.rentSpread || 0);
  }, [currentRentForecast?.rentSpread]);

  useEffect(() => {
    if (!currentRentForecast?.id || !rows.length) return;

    // This hook is the last effect to run when rows are updated.
    // Make sure calculation isn't triggered when current rent forecast is changing
    if (loadingFloorPlans.current) {
      loadingFloorPlans.current = false;
      return;
    }

    const nextPayload = rows.map((r) => {
      const payloadRow = {
        floorPlanId: r.id,
        newNoteRentProposedChangeValue:
          r?.newnoteRentProposedChangeValue?.value || 0,
        newBasicRentProposedChangeValue:
          r?.newbasicRentProposedChangeValue?.value || 0,
        newRDUtilityAllowanceProposedChangeValue:
          r?.['newRD Utility AllowanceProposedChangeValue']?.value || 0,
        newHUDGrossRentProposedChangeValue:
          r?.newHUDGrossRentProposedChangeValue?.value || 0,
        newNonOptionalCharge: r?.newnonOptionalCharge?.value || 0,
        newNonOptionalChargeProposedChangeValue:
          r?.newnonOptionalChargeProposedChangeValue?.value || 0,
        newHud236BasicRentProposedChangeValue:
          r?.newhud236BasicRentProposedChangeValue?.value || 0,
        newHud236BasicRent: r?.newhud236BasicRent?.value || 0,
        newHud236MarketRent: r?.newhud236MarketRent?.value || 0,
        newTHDAUtilityAllowance: r?.['newTHDA Utility Allowance']?.value || 0,
        newBaseMarketRentAmountProposedChangeValue:
          r?.newbaseMarketRentAmountProposedChangeValue?.value || 0,
        newQuotingRentAmountProposedChangeValue:
          r?.newquotingRentAmountProposedChangeValue?.value || 0,
        newEstimatedDepositAmountProposedChangeValue:
          r?.newestimatedDepositAmountProposedChangeValue?.value || 0,
        newTHDAUtilityAllowanceProposedChangeValue:
          r?.['newTHDA Utility AllowanceProposedChangeValue']?.value || 0,
        newHud236MarketRentProposedChangeValue:
          r?.newhud236MarketRentProposedChangeValue?.value || 0,
        newFinancialMarketRentAmountProposedChangeValue:
          r?.newfinancialMarketRentAmountProposedChangeValue?.value || 0,
        newFinancialMarketRentAmount:
          r?.newfinancialMarketRentAmount?.value || 0,
        newHudGrossRentProposedChange:
          r?.newHudGrossRentProposedChange?.value || 0,
        newQuotingRentAmount: r?.newQuotingRentAmount?.value || 0,
        newQuotingRentAmountPotentialIncome:
          r?.newQuotingRentAmountPotentialIncome?.value || 0,
        newEstimatedDepositAmountProposedChange:
          r?.newEstimatedDepositAmountProposedChange?.value || 0,
        newUtilityAllowanceProposedChange:
          r?.newUtilityAllowanceProposedChange?.value || 0,
      };

      return payloadRow;
    });
    const proposalTypeChanged =
      selectedProposedChangeType !== prevTypeRef.current;
    const forecastChanged =
      currentRentForecast?.id !== prevForecastSync.current;

    if (
      isUpdatingDirect ||
      prevPayloadRef.current.length === 0 ||
      skipNextSync.current ||
      proposalTypeChanged ||
      forecastChanged
    ) {
      prevPayloadRef.current = nextPayload;
      prevTypeRef.current = selectedProposedChangeType;
      prevForecastSync.current = currentRentForecast?.id;
      skipNextSync.current = false;
      return;
    }

    if (!isEqual(prevPayloadRef.current, nextPayload)) {
      // Do not save changes if selected forecast is approved
      if (!currentRentForecast?.approvedFrom) {
        submitDirectRentUpdate(nextPayload);
      }
      prevPayloadRef.current = nextPayload;
    }
    prevTypeRef.current = selectedProposedChangeType;
  }, [rows, currentRentForecast?.id, submitDirectRentUpdate, isUpdatingDirect]);

  const currentRentForecastId = currentRentForecast?.id;

  const selectValues = rentForecasts?.rentForecasts?.map((rentForecast) => ({
    value: rentForecast?.id,
    text: getForecastName(rentForecast, forecastsWithApprovalNumbers),
  }));

  const selectedValue =
    selectValues?.find((value) => value?.value === currentRentForecastId)
      ?.value || '';

  const selectedRentForecastName = getForecastName(
    currentRentForecast,
    forecastsWithApprovalNumbers,
  );

  const handleSelect = (value) => {
    const newRentForecast = rentForecasts?.rentForecasts?.find(
      (rentForecast) => rentForecast?.id === value,
    );
    setCurrentRentForecast(newRentForecast);
  };

  const buildZeroState = (rows) => {
    const zero = 0;
    const acc = {};
    rows.forEach((row) => {
      Object.keys(row).forEach((col) => {
        if (col.startsWith('new') && col.includes('ProposedChange')) {
          acc[`${row.id}-${col.replace(/Value$/, '')}`] = zero;
        }
      });
    });
    return acc;
  };

  const buildZeroPayload = (rows, usePercent) =>
    rows
      .filter((r) => !!r.id)
      .map((r) => ({
        floorPlanId: r.id,
        newNoteRentProposedChangeValue: usePercent ? 1 : 0,
        newBasicRentProposedChangeValue: usePercent ? 1 : 0,
        newRDUtilityAllowanceProposedChangeValue: usePercent ? 1 : 0,
        newHUDGrossRentProposedChangeValue: usePercent ? 1 : 0,
        newNonOptionalCharge: usePercent ? 1 : 0,
        newHud236BasicRent: usePercent ? 1 : 0,
        newHud236BasicRentProposedChangeValue: usePercent ? 1 : 0,
        newHud236MarketRent: usePercent ? 1 : 0,
        newNonOptionalChargeProposedChangeValue: usePercent ? 1 : 0,
        newTHDAUtilityAllowance: usePercent ? 1 : 0,
        newTHDAUtilityAllowanceProposedChangeValue: usePercent ? 1 : 0,
        newHud236MarketRentProposedChangeValue: usePercent ? 1 : 0,
        newBaseMarketRentAmountProposedChangeValue: usePercent ? 1 : 0,
        newQuotingRentAmountProposedChangeValue: usePercent ? 1 : 0,
        newEstimatedDepositAmountProposedChangeValue: usePercent ? 1 : 0,
        newFinancialMarketRentAmountProposedChangeValue: usePercent ? 1 : 0,
        newFinancialMarketRentAmount: usePercent ? 1 : 0,
        newHudGrossRentProposedChange: usePercent ? 1 : 0,
        newQuotingRentAmount: usePercent ? 1 : 0,
        newQuotingRentAmountPotentialIncome: usePercent ? 1 : 0,
        newEstimatedDepositAmountProposedChange: usePercent ? 1 : 0,
        newUtilityAllowanceProposedChange: usePercent ? 1 : 0,
      }));

  const [proposedChangesSubmitting, setProposedChangesSubmitting] =
    useState(false);
  const [rentSpreadValue, setRentSpreadValue] = useState(
    currentRentForecast?.rentSpread,
  );

  const handleProposedChangesSubmit = (proposedChangeType) => {
    resetProposedState(buildZeroState(rows));
    submitDirectRentUpdate(buildZeroPayload(rows, usePercent), {
      skipToast: true,
    });
    skipNextSync.current = true;
    setSelectedProposedChangeType(proposedChangeType);
    updateRentForecast(
      {
        id: currentRentForecastId,
        proposedChangeType,
        rentSpread: null,
      },
      {
        onSuccess: () => {
          setProposedChangesSubmitting(false);
          setRentSpreadValue(0);
          closeProposedChangesModal();
        },
        onError: () => {
          setProposedChangesSubmitting(false);
          closeProposedChangesModal();
        },
      },
    );
  };

  const handleRentSpreadChange = (value) => {
    setProposedState((prevState) => {
      const changes = rows.reduce((acc, row) => {
        return {
          ...acc,
          [`${row.id}-newnoteRentProposedChange`]: value,
        };
      }, {});

      return { ...prevState, ...changes };
    });
  };

  const handleVacancyRateChange = (value) => {
    setCurrentRentForecast((prev) => ({
      ...prev,
      vacancyRate: value,
    }));
  };

  const refreshAndGotoNext = () => {
    if (!currentRentForecast || !rentForecasts.rentForecasts?.length) {
      return;
    }

    const currentIndex = rentForecasts.rentForecasts.findIndex(
      (rf) => rf.id === currentRentForecast.id,
    );

    if (currentIndex < 0) {
      return;
    }

    // There was only one rent forecast, just refresh
    if (rentForecasts.rentForecasts.length === 1) {
      setCurrentRentForecastId('');
      reloadRentForecastList();

      return;
    }

    setCurrentRentForecastId(
      // Go to first forecast if last was removed, otherwise go to next one
      currentIndex + 1 === rentForecasts.rentForecasts.length
        ? rentForecasts.rentForecasts[0].id
        : rentForecasts.rentForecasts[currentIndex + 1].id,
    );

    reloadRentForecastList();
  };

  const unoccupiedUnits = unitInfo?.totalUnits - unitInfo?.occupiedUnits;
  const vacancyRateActual = `${(
    (unoccupiedUnits / unitInfo?.totalUnits) *
    100
  ).toFixed(2)}%`;

  const [utilityAllowanceTableOpen, setUtilityAllowanceTableOpen] =
    useState(false);

  const usePercent = selectedProposedChangeType === 'percent';

  return (
    <DocumentTitle title={intl.formatMessage(messages.title)}>
      <>
        <TableLayout
          name={tableName}
          sx={{
            'button[disabled]': { backgroundColor: 'unset' },
          }}
          childrenElementsHeight={50 + 8 * 2}
        >
          {(tableHeight) => (
            <>
              <RentForecastHeader
                openRentForecastModal={openRentForecastModal}
                disableButtonModal={isQueryLoading || !hasEditPermission}
                count={count}
                totalCount={totalCount}
              />
              {rdBudgets && isRD && (
                <Box
                  sx={{
                    display: 'flex',
                    visibility: selectedValue ? 'visible' : 'hidden',
                    marginBottom: 2,
                  }}
                >
                  <Card
                    title={messages.vacancyRate}
                    actualValue={selectedValue ? vacancyRateActual : '---'}
                    proposedValue={currentRentForecast?.vacancyRate}
                    valueKey="vacancyRate"
                    updateRentForecast={updateRentForecast}
                    currentRentForecastId={currentRentForecastId}
                    adornment={<Percent />}
                    tooltip={messages.vacancyRateTooltip}
                    sx={{ marginRight: 1 }}
                    onChangeApplied={handleVacancyRateChange}
                    disabled={!canEdit}
                  />
                  <Card
                    title={messages.rentSpread}
                    subtitle={messages.rentSpreadSub}
                    proposedValue={rentSpreadValue}
                    valueKey="rentSpread"
                    updateRentForecast={updateRentForecast}
                    currentRentForecastId={currentRentForecastId}
                    adornment={usePercent ? <Percent /> : <MoneyIcon />}
                    tooltip={messages.rentSpreadTooltip}
                    sx={{ marginRight: 1 }}
                    onChangeApplied={handleRentSpreadChange}
                    disabled={!canEdit}
                  />
                  {currentRentForecast?.approvedFrom && (
                    <TransmissionCard
                      disabled={!hasEditPermission}
                      updateRentForecast={updateRentForecast}
                      initialValues={pick(currentRentForecast, [
                        'transmissionDate',
                        'transmissionNumber',
                      ])}
                    />
                  )}
                </Box>
              )}
              {currentRentForecast?.approvedFrom && (
                <Banner
                  color="blue"
                  icon={<AlertInfoIcon />}
                  text={intl.formatMessage(messages.approvedBannerInfo)}
                  hasBorder={false}
                />
              )}
              <RentForecastTable
                toolbarActions={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      visibility: selectValues?.length ? 'visible' : 'hidden',
                    }}
                  >
                    <SingleSelect
                      name=""
                      options={selectValues}
                      onChange={handleSelect}
                      value={selectedValue}
                      sx={{
                        maxWidth: 200,
                      }}
                      HelperTextProps={{
                        sx: { display: 'none' },
                      }}
                    />
                    {rdBudgets && isRD && (
                      <BudgetActionsDropdown
                        disabled={!totalCount}
                        currentForecast={currentRentForecast}
                        onReview={openRDBudgetModal}
                        onUtility={() => setUtilityAllowanceTableOpen(true)}
                        onLetter={openResidentLetterModal}
                        onAuthorizeSignature={openAuthorizeSignatureModal}
                        onApproveBudget={openApproveBudgetModal}
                        onBuildBudget={openBuildBudgetModal}
                        onReportHistory={openReportHistoryModal}
                        canEdit={canEdit}
                      />
                    )}

                    <Button
                      onClick={openProposedChangesModal}
                      disabled={!totalCount}
                      sx={{
                        minWidth: 'fit-content',
                      }}
                    >
                      {intl.formatMessage(messages.proposedChanges)}
                    </Button>
                  </Stack>
                }
                rightToolbarActions={
                  <Stack direction="row" spacing={1}>
                    <DeleteForecastButton
                      rentForecastId={currentRentForecastId}
                      rentForecastName={getForecastName(
                        currentRentForecast,
                        forecastsWithApprovalNumbers,
                      )}
                      onSuccess={refreshAndGotoNext}
                      disabled={!rentForecasts?.rentForecasts.length}
                      hasApprovals={hasApprovals}
                    />
                  </Stack>
                }
                key={currentRentForecastId}
                allColumnsHidden={allColumnsHidden}
                columnOptions={columnOptions}
                count={count}
                currentRentForecast={currentRentForecast}
                dateState={dateState}
                filterState={filterState}
                filterTypeState={filterTypeState}
                filteredHeaders={filteredHeaders}
                footerRow={footerRow}
                handleColumnChange={handleColumnChange}
                handleDateSubmit={handleDateSubmit}
                handleFilterChange={handleFilterChange}
                handleFilterTypeChange={handleFilterTypeChange}
                handleSearchSubmit={handleSearchSubmit}
                handleSortChange={handleSortChange}
                handleSettingsClose={handleSettingsClose}
                headers={headers}
                height={tableHeight}
                intl={intl}
                name={tableName}
                onCSVButtonClick={onCSVButtonClick}
                order={order}
                orderBy={orderBy}
                rows={rows}
                searchState={searchState}
                selectedColumns={selectedColumns}
                totalCount={totalCount}
                isLoading={isQueryLoading}
              />
            </>
          )}
        </TableLayout>
        <RentForecastModal
          open={rentForecastOpen}
          closeModal={closeRentForecastModal}
          createRentForecast={createRentForecast}
          isLoading={isLoading}
          rentForecastsData={rentForecasts}
        />
        <RDBudgetModal
          key={currentRentForecast?.id || 'noid'}
          open={rdBudgetOpen}
          closeModal={closeRDBudgetModal}
          forecastName={selectedRentForecastName}
          rentForecast={currentRentForecast}
          intacctOpen={intacctOpen}
          openIntacctModal={openIntacctModal}
          closeIntacctModal={closeIntacctModal}
          manageRentForecastData={manageRentForecastData}
          vacancyRateActual={vacancyRateActual}
          newbasicRentPotentialIncome={
            footerRow.newbasicRentPotentialIncome.value
          }
          totalUnits={unitInfo?.totalUnits}
          fpRows={rows}
          changeType={selectedProposedChangeType}
          hasEditPermission={hasEditPermission}
        />
        <UtilityAllowanceTableModal
          rentForecasts={rentForecasts}
          currentRentForecast={currentRentForecast}
          rentForecastTableRows={rows}
          open={utilityAllowanceTableOpen}
          setProposedState={setProposedState}
          canEdit={canEdit}
          closeModal={() => setUtilityAllowanceTableOpen(false)}
        />
        <ProposedChangesModal
          open={proposedChangesOpen}
          closeModal={closeProposedChangesModal}
          onSubmit={handleProposedChangesSubmit}
          selectedProposedChangeType={selectedProposedChangeType}
          isSubmitting={proposedChangesSubmitting}
          disableChange={!canEdit}
        />
        <ResidentLetterModal
          open={residentLetterOpen}
          closeModal={closeResidentLetterModal}
          rows={rows}
          rentForecast={currentRentForecast}
          isPercent={usePercent}
        />
        <AuthorizeSignatureModal
          open={authorizeSignatureOpen}
          closeModal={closeAuthorizeSignatureModal}
          currentRentForecast={currentRentForecast}
        />
        <ApproveBudgetModal
          open={approveBudgetOpen}
          closeModal={closeApproveBudgetModal}
          rentForecastId={currentRentForecastId}
          reloadRentForecastList={reloadRentForecastList}
        />
        <BuildBudget
          open={buildBudgetOpen}
          closeModal={closeBuildBudgetModal}
          rentForecast={currentRentForecast}
          reloadRentForecastList={reloadRentForecastList}
          rentForecastsData={rentForecasts}
        />
        <ReportHistoryModal
          open={reportHistoryOpen}
          closeModal={closeReportHistoryModal}
          rentForecastId={currentRentForecastId}
        />
      </>
    </DocumentTitle>
  );
};

export default RentForecast;
